import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  addLineToShopifyCart,
  createShopifyCart,
  fetchCartStatus,
  removeLineFromShopifyCart,
  updateShopifyCartLine,
  type ShopifyCartSnapshot,
  type ShopifyImage,
} from "@/lib/shopify";

export interface CartItem {
  lineId: string | null;
  variantId: string;
  productHandle: string;
  productTitle: string;
  variantTitle: string;
  image: ShopifyImage | null;
  price: { amount: string; currencyCode: string };
  quantity: number;
  /** Remaining on-hand units at time of add/sync. Null when merchant hides inventory or pre-order is allowed. */
  maxQuantity?: number | null;
  selectedOptions: Array<{ name: string; value: string }>;
  /** Selling plan attached for Stoq pre-order variants. */
  sellingPlanId?: string | null;
  /** Line-item properties (e.g. Chapter Vials fragrance picks). */
  attributes?: Array<{ key: string; value: string }>;
}


interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  addItem: (item: Omit<CartItem, "lineId">) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
}

function cartItemsFromShopifyCart(cart: ShopifyCartSnapshot): CartItem[] {
  return cart.lines.edges.reduce<CartItem[]>((items, { node }) => {
    const variant = node.merchandise;
    if (!variant?.id || !variant.product) return items;
    const trackedMax =
      typeof variant.quantityAvailable === "number" && !variant.currentlyNotInStock
        ? variant.quantityAvailable
        : null;
    items.push({
      lineId: node.id,
      variantId: variant.id,
      productHandle: variant.product.handle,
      productTitle: variant.product.title,
      variantTitle: variant.title,
      image: variant.image ?? variant.product.featuredImage ?? null,
      price: variant.price,
      quantity: node.quantity,
      maxQuantity: trackedMax,
      selectedOptions: variant.selectedOptions ?? [],
    });
    return items;
  }, []);
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existing = items.find((i) => i.variantId === item.variantId);
        set({ isLoading: true });
        try {
          if (!cartId) {
            const result = await createShopifyCart({
              variantId: item.variantId,
              quantity: item.quantity,
              sellingPlanId: item.sellingPlanId ?? null,
              attributes: item.attributes,
            });
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...item, lineId: result.lineId }],
              });
            }
          } else if (existing && existing.lineId && !item.attributes?.length) {
            const cap = item.maxQuantity ?? existing.maxQuantity ?? null;
            const desired = existing.quantity + item.quantity;
            const newQty = typeof cap === "number" ? Math.min(desired, cap) : desired;
            const r = await updateShopifyCartLine(cartId, existing.lineId, newQty);
            if (r.success) {
              set({
                items: get().items.map((i) =>
                  i.variantId === item.variantId
                    ? { ...i, quantity: newQty, maxQuantity: cap }
                    : i,
                ),
              });
            } else if (r.cartNotFound) clearCart();
          } else {
            const r = await addLineToShopifyCart(cartId, {
              variantId: item.variantId,
              quantity: item.quantity,
              sellingPlanId: item.sellingPlanId ?? null,
              attributes: item.attributes,
            });
            if (r.success) {
              set({ items: [...get().items, { ...item, lineId: r.lineId ?? null }] });
            } else if (r.cartNotFound) clearCart();
          }
        } finally {
          set({ isLoading: false });
        }
      },


      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) return get().removeItem(variantId);
        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const r = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (r.success) {
            set({
              items: get().items.map((i) => (i.variantId === variantId ? { ...i, quantity } : i)),
            });
          } else if (r.cartNotFound) clearCart();
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const r = await removeLineFromShopifyCart(cartId, item.lineId);
          if (r.success) {
            const next = get().items.filter((i) => i.variantId !== variantId);
            if (next.length === 0) clearCart();
            else set({ items: next });
          } else if (r.cartNotFound) clearCart();
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),
      getCheckoutUrl: () => get().checkoutUrl,

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;
        set({ isSyncing: true });
        try {
          const result = await fetchCartStatus(cartId);
          // Only clear when Shopify confirms the cart is gone or completed.
          // Transient errors (network, 402, throttling) preserve local items.
          if (result.status === "missing") {
            clearCart();
          } else if (result.status === "ok") {
            const syncedItems = cartItemsFromShopifyCart(result.cart);
            if (syncedItems.length > 0) {
              set({
                items: syncedItems,
                cartId: result.cart.id,
                checkoutUrl: result.cart.checkoutUrl,
              });
            } else {
              // A zero-line cart response is not enough proof that a customer completed
              // checkout; keep any local items unless Shopify says the cart is missing.
              set({ checkoutUrl: result.cart.checkoutUrl });
            }
          }
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: "shopify-cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ items: s.items, cartId: s.cartId, checkoutUrl: s.checkoutUrl }),
    },
  ),
);
